import * as Sentry from '@sentry/react'
import { accessTokens } from 'api/accessTokenMgmt'
import { isAndroid, isIOS } from 'lib/device'
import { createReplySubmitDiagnostics, describeTarget } from 'utils/replySubmitDiagnosticsCore'
import type { CrumbData } from 'utils/replySubmitDiagnosticsCore'

const TAP_EVENTS = [
    'pointerup',
    'pointercancel',
    'touchend',
    'touchcancel',
    'click',
    'focusin',
    'focusout',
] as const

function listen(crumb: (event: string, data?: CrumbData) => void) {
    const onEvent = (event: Event) => {
        const pointer = event as PointerEvent
        crumb(event.type, {
            target: describeTarget(event.target),
            ...(event.type.startsWith('focus')
                ? { related: describeTarget((event as FocusEvent).relatedTarget) }
                : {}),
            ...(event.type.startsWith('pointer') ? { pointer_type: pointer.pointerType } : {}),
            prevented: event.defaultPrevented,
        })
    }
    const onResize = () =>
        crumb('viewport_resize', { vv_height: Math.round(window.visualViewport?.height ?? 0) })
    TAP_EVENTS.forEach(type => document.addEventListener(type, onEvent, true))
    window.visualViewport?.addEventListener('resize', onResize)
    return () => {
        TAP_EVENTS.forEach(type => document.removeEventListener(type, onEvent, true))
        window.visualViewport?.removeEventListener('resize', onResize)
    }
}

export const replySubmitDiagnostics = createReplySubmitDiagnostics({
    enabled: () => typeof window !== 'undefined' && (isAndroid || isIOS),
    listen,
    report: (kind, crumbs) => {
        const webCommit = (
            window as unknown as {
                Knov?: { version?: { gitCommitHash?: string } }
            }
        ).Knov?.version?.gitCommitHash
        const nativeShell = !!(window as unknown as { ReactNativeWebView?: unknown })
            .ReactNativeWebView
        // Some phones never reach Sentry (an ad-blocking DNS, for one); our own
        // API always does, and its log reaches Graylog.
        try {
            void fetch(`${window.knovApiUrl}/api/v1/reply_submit_diagnostics`, {
                method: 'POST',
                headers: { ...accessTokens(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kind,
                    platform: isAndroid ? 'android' : isIOS ? 'ios' : undefined,
                    native_shell: nativeShell,
                    web_commit: webCommit,
                    crumbs,
                }),
                keepalive: true,
            }).catch(() => {})
        } catch {
            // Reporting must never affect posting.
        }
        Sentry.captureMessage(`[reply-submit] ${kind}`, {
            level: 'warning',
            tags: {
                reply_submit: kind,
                ...(typeof webCommit === 'string' && /^[a-f0-9]{7,40}$/.test(webCommit)
                    ? { web_commit: webCommit }
                    : {}),
                native_shell: String(nativeShell),
            },
            extra: { crumbs },
        })
    },
})
